import { m, verbatim, type Message, type PlainKey } from '@/i18n';
import type { Level } from './constants';
import { LANE_ATTRIBUTE_VALUES, laneValueOf, type LaneAttribute } from './lane-attributes';
import {
  edgeFactsOf,
  resolve,
  stimulusOf,
  type Context,
  type PendingStimulus,
  type Plan,
  type Question,
  type Resolution,
  type StageFacts,
} from './resolution';
import { EDGE_VERB, lifecycleEdges, type Edge, type EdgeKind } from './lifecycle-edges';
import { describeMeans, type MeansPair } from './stage-gates';
import { errorMessage } from './types';

/**
 * WHAT A SURFACE MAY OFFER FROM THE WORN STATE (Leon, 2026-09-21,
 * docs/DESIGN_SYSTEM.md "Menu grammar"; put on the resolver 2026-09-21,
 * docs/RESOLUTION.md §12 step 5): the act sheet's verbs, and the value rows the
 * state line's chips and its "+" wear.
 *
 * **The acts FOLLOW from the attributes.** The state line wears the values
 * (`domain/state-chips.ts` decides which axes speak, and a chip's menu is that
 * axis's own words); this module decides what you can DO from there. One
 * derivation, so the two registers can never disagree about an item: the line
 * says what is, the sheet says what is takeable, and no surface holds a second
 * definition of either.
 *
 * **It decides no availability of its own.** Which acts BELONG on the sheet, in
 * which order, and which of them the seat rule locks is this module's; whether
 * one is takeable HERE, and why not, is the DRY RUN's answer - `resolve` over
 * the surface's own context, the same pure table the server runs inside its
 * transaction (§9: "gated targets in menus, disabled with reasons"). Until this
 * day each menu folded its own command sequences through `decide` and dropped
 * whatever came back illegal, which is how a menu could offer an act the spine
 * then refused and hide one it would have taken.
 *
 * A row therefore carries its STIMULUS, not a command list: the surface mints
 * the gesture's key at the tap, re-runs the same pure table with it, and sends
 * the stimulus plus the plan id it previewed.
 *
 * **Which acts belong is the EDGE TABLE's** (`domain/lifecycle-edges.ts`,
 * Leon 2026-11-03): the item's position for this viewer says its edges, the
 * forward one is the pill (`PrimaryAct`) and the rest are the rows. This module
 * adds a member's stage move and prices every row by the dry run; it holds no
 * rule of its own about which ending is offered to whom. A flow whose stage
 * hands the work in owns Ready for review (the table's `deliveryCovered`), and
 * the activity axis keeps its own rows (`activityRows`).
 *
 * **What a ROW says is here too** (`rowState`, moved off the surface
 * 2026-09-22): disabled or not, which lock, which sub-line, which grade. It is
 * the same question this module already answers, one row at a time, and both
 * registers read it - the acts here, the values in `domain/state-line.ts` -
 * so a refused act reads the same wherever it is offered. The surface adds the
 * glyphs and the words around it and nothing else.
 */

/** The acts this surface can hold: the six edges of the finishing lifecycle
 *  (`domain/lifecycle-edges.ts`), the activity axis's two, and a member's
 *  stage move. One verb each, and the UI maps the kind to its glyph; the
 *  DISPATCH is the row's own stimulus. */
export type ActKind = EdgeKind | 'start' | 'pause' | 'advance';

/** The act words, in one place: a row and the pill that leads it read the same
 *  map, so one tap can never wear two words. The edges' words are the edge
 *  table's own (`EDGE_VERB`). */
export const ACT_VERB: Record<ActKind, PlainKey> = {
  ...EDGE_VERB,
  start: 'acts.verb.start',
  pause: 'acts.verb.pause',
  advance: 'acts.verb.advance',
};

/**
 * The dry run's answer about ONE candidate, in the terms a menu row renders
 * (§9). Every field here comes from `resolve`; none is a second judgment.
 */
export interface Offer {
  /** Takeable from here: the resolution is a plan, or a QUESTION the surface
   *  can ask before it acts (§6's conclusion guard is not a refusal). */
  enabled: boolean;
  /** Why not - the refusal's or the lane's own words, surfaced verbatim. */
  reason?: Message;
  /** A missing right is a lane, never a dead end (§1 step 5). */
  lane?: 'propose' | 'request-access';
  /** `decide`'s grade on the plan's steps: the out-of-role accessory word. */
  level?: Level;
  /** What the act costs beyond its word (the menu grammar's rule 4) - the
   *  plan's own consequence, or the question it will ask. */
  price?: Message;
  /** The question this act runs into; the surface asks it and repeats the
   *  stimulus with the answer. */
  question?: Question;
  plan?: Plan;
  /** Nothing to do: the item is already there. A VALUE row is the worn one; an
   *  act row does not render at all. */
  noChange: boolean;
}

export interface ActRow extends Offer {
  kind: ActKind;
  /** The act's word (`ACT_VERB`), as a descriptor. */
  verb: Message;
  /** What the row sends, minus the key the surface mints at the tap. */
  stimulus: PendingStimulus;
  /** The edge is not this viewer's (the edge table's `locked`): Cancel off the
   *  requester seat, as since 2026-09-20 (an act nobody can see is an act
   *  nobody can find) - disabled, wearing the seat word and the lock.
   *  Everything else about the row comes from the dry run. */
  seatLocked: boolean;
  /** The danger register: the way down wears it, and the glyph says it too. */
  danger: boolean;
  /** The quiet register (the edge table's `quiet`): the way back as the
   *  pill wears the outline, never the accent fill. */
  quiet: boolean;
}

export interface ActSheet {
  /** The rows, in the order they are offered. Start and Pause are not among
   *  them since 2026-11-03: they are `activityRows`, in the "…" sheet. */
  rows: ActRow[];
  /** The page's PRIMARY act (`PrimaryAct`), or null when the viewer has none
   *  here. */
  primary: PrimaryAct | null;
}

/**
 * **THE PRIMARY ACT**: what the item head's accent pill and the dock's push in
 * one tap.
 *
 * - the FORWARD EDGE of the item's position for this viewer
 *   (`domain/lifecycle-edges.ts`, Leon 2026-11-03): Done on an open item,
 *   Ready for review under a review duty, Accept on an item in review for its
 *   reviewer, Reopen on a done or cancelled item. Nothing for anyone else in
 *   review. It is the same gesture the checklist's box is, through the same
 *   table, so the page and a checklist cannot mean two things by it;
 * - TAKE UP, for a viewer a seat is offered to (`offered`).
 *
 * Start and Pause are never it: the activity axis stays reachable in the "…"
 * sheet (`activityRows`), never the default gesture (2026-11-03).
 */
export type PrimaryAct = { kind: 'edge'; row: ActRow } | { kind: 'takeUp' };

/** One value of one axis, with what the dry run says about moving there. */
export interface ValueOffer {
  attribute: LaneAttribute;
  value: string;
  /** The value word, as a descriptor (docs/I18N.md §6). */
  label: Message;
  /** The item wears this value now: the checked, inert row. */
  worn: boolean;
  offer: Offer;
}

/**
 * The key a DRY RUN runs under. It writes nothing, and the plan id it computes
 * is the preview's own - the surface re-runs the table with the gesture's real
 * key at the tap, because the determinism check hashes that key (§1 step 7).
 */
const PREVIEW_KEY = 'preview';

/** One candidate through the resolver, for rendering only. Pure. */
export function dryRun(pending: PendingStimulus, itemId: string, context: Context): Resolution {
  return resolve(
    stimulusOf(pending, itemId, { key: PREVIEW_KEY, source: 'person', surface: 'preview' }),
    context,
  );
}

/** The worst grade across a plan's steps: the word the row wears. */
function levelOf(plan: Plan): Level | undefined {
  if (plan.steps.some((s) => s.level === 'critical')) return 'critical';
  if (plan.steps.some((s) => s.level === 'warning')) return 'warning';
  return undefined;
}

/** What a question costs the person, said in one short line rather than
 *  restating the whole question in a sub-line. */
function priceOfQuestion(question: Question): Message | undefined {
  return question.class === 'open-children' ? question.text : undefined;
}

/** A resolution, in the terms a menu row renders. One mapping for every
 *  register, so a refused act reads the same wherever it is offered. */
export function offerOf(resolution: Resolution): Offer {
  switch (resolution.kind) {
    case 'plan': {
      const plan = resolution.plan;
      const level = levelOf(plan);
      const price = plan.confirm ? plan.confirm.text : plan.consequences[0];
      return {
        enabled: true,
        noChange: false,
        plan,
        ...(level ? { level } : {}),
        ...(price ? { price } : {}),
        ...(plan.confirm ? { question: plan.confirm } : {}),
      };
    }
    case 'question': {
      const price = priceOfQuestion(resolution.question);
      return {
        enabled: true,
        noChange: false,
        question: resolution.question,
        ...(price ? { price } : {}),
      };
    }
    case 'lane':
      return {
        enabled: false,
        noChange: false,
        lane: resolution.lane,
        reason: resolution.reason,
      };
    case 'refused':
      return {
        enabled: false,
        noChange: resolution.error.code === 'no_change',
        reason: errorMessage(resolution.error),
      };
  }
}

/** The two grades a ROW wears (Prinzip 20, flag-not-block): `decide`'s
 *  out-of-role flags. Every other grade says nothing on a row. */
export type OutOfRoleLevel = 'warning' | 'critical';

/** The grade a row wears, or nothing. */
export function outOfRoleLevel(level: Level | undefined): OutOfRoleLevel | undefined {
  return level === 'warning' || level === 'critical' ? level : undefined;
}

/**
 * ONE OFFER, in the parts a menu row is built from: whether it can be taken,
 * which lock it wears, what it costs, and the grade it carries. Both registers
 * read it, so a refused act reads the same wherever it is offered, and the
 * surface adds only the glyphs and the words around it.
 *
 * LOCKED, never absent (Leon, 2026-09-20, amending the SURFACE half of the
 * 2026-07-25 residency ruling): a viewer without the requester seat still SEES
 * the explicit conclusion - the same row, disabled, wearing the seat word and
 * the lock (a conclusion that is the tick locks nothing, 2026-09-27);
 * a viewer who may not act at all wears step 5's lane instead (§1). Hiding
 * either taught the phone that the act did not exist. The MODEL is untouched:
 * `decide` grades an out-of-role conclusion exactly as before.
 */
export interface RowState {
  disabled: boolean;
  /** `seat` is the conclusion's residency rule, `view` is step 5's lane. */
  lock?: 'seat' | 'view';
  /** The lane's own words, for the lock's explainer. */
  lockReason?: Message;
  /** The sub-line: what the act costs where it is takeable (the menu grammar's
   *  rule 4), the refusal's own words where it is not. A LOCKED row has none -
   *  it is not priced, it is closed. */
  line?: Message;
  /** The out-of-role grade, as the word the row wears. */
  flagged?: OutOfRoleLevel;
}

export function rowState(offer: Offer, opts: { seatLocked?: boolean } = {}): RowState {
  if (opts.seatLocked) return { disabled: true, lock: 'seat' };
  if (offer.lane === 'request-access') {
    return {
      disabled: true,
      lock: 'view',
      ...(offer.reason ? { lockReason: offer.reason } : {}),
    };
  }
  // A gated target states its reason on the sub-line, the way the stage menu's
  // refused rows already do (docs/FLOW_DEFINITION.md ruling §7.5); a takeable
  // one states its price, and an out-of-role one wears the word.
  const line = offer.enabled ? offer.price : (offer.price ?? offer.reason);
  const flagged = outOfRoleLevel(offer.level);
  return {
    disabled: !offer.enabled,
    ...(line ? { line } : {}),
    ...(flagged ? { flagged } : {}),
  };
}

/** Whether a flow stage covers an atom through its `means` - the test that
 *  keeps a member's sheet from offering an act the stage move applies. */
export function meansCover(
  stages: readonly { means: readonly { attr: string }[] }[],
  attr: 'delivered' | 'closed' | 'active',
): boolean {
  return stages.some((s) => s.means.some((p) => p.attr === attr));
}

/** The advance's sub-line: where it lands, and what landing there applies -
 *  or, when the boundary refuses, why. */
function advancePrice(
  stage: { label: string; means: readonly MeansPair[] },
  offer: Offer,
): Message {
  if (!offer.enabled && offer.reason) {
    return m('acts.advance.refused', { stage: stage.label, reason: offer.reason });
  }
  const means = describeMeans(stage.means);
  return means ? m('acts.advance.applies', { stage: stage.label, means }) : verbatim(stage.label);
}

function row(
  kind: ActKind,
  stimulus: PendingStimulus,
  itemId: string,
  context: Context,
  over: Partial<ActRow> = {},
  resolution: Resolution = dryRun(stimulus, itemId, context),
): ActRow {
  return {
    kind,
    verb: m(ACT_VERB[kind]),
    stimulus,
    seatLocked: false,
    danger: false,
    quiet: false,
    ...offerOf(resolution),
    ...over,
  };
}

/**
 * The act sheet for one item: the edges the table stands here (the forward
 * one is the pill's, the rest are the rows), a member's stage move, and the
 * dry run's answer about each.
 *
 * `flows` is the member's own flow structure (the stages and their `means`),
 * which the context carries only for the stage a move targets.
 */
export function actSheet({
  itemId,
  context,
  offered = false,
}: {
  itemId: string;
  /** The resolver's context, assembled from the live state and the page's
   *  facts (`assembleContext`). */
  context: Context;
  /** The stages of the flows the item belongs to (unused by the rows since
   *  the edge table reads the flows off the context, 2026-11-03). */
  flows?: readonly { rows: readonly StageFacts[] }[];
  /** A seat on this item is offered to the viewer (or a group of theirs):
   *  the primary act is to take it up. */
  offered?: boolean;
}): ActSheet {
  const edges = lifecycleEdges(edgeFactsOf(context));
  const rows: ActRow[] = [];

  // A member's forward step through its flow is the stage move; the atoms keep
  // the edges the flow does not own.
  const membership = context.memberships[0];
  const target = membership?.target;
  if (membership && target && !context.item.closed) {
    const advance = row(
      'advance',
      { kind: 'move', flowId: membership.flowId, toStageKey: target.key },
      itemId,
      context,
    );
    rows.push({ ...advance, price: advancePrice(target, advance) });
  }

  let primary: PrimaryAct | null = offered ? { kind: 'takeUp' } : null;
  for (const edge of edges) {
    const r = edgeRow(edge, itemId, context);
    if (edge.forward && !primary) {
      // The pill is the forward edge where the dry run would take it; a
      // refusal or a lane is no primary act, and the row stays in the sheet.
      if (r.enabled && r.lane === undefined) {
        primary = { kind: 'edge', row: r };
        continue;
      }
    }
    if (edge.forward && primary?.kind === 'edge') continue;
    rows.push(r);
  }
  // A no-op act has no row: "already there" is what the line says, not a
  // disabled verb (a refusal with a reason still renders, disabled).
  return { rows: rows.filter((r) => !r.noChange), primary };
}

/** One edge of the table as an act row, priced by the dry run. */
export function edgeRow(edge: Edge, itemId: string, context: Context): ActRow {
  return row(edge.kind, edge.stimulus, itemId, context, {
    seatLocked: edge.locked,
    danger: edge.danger,
    quiet: edge.quiet,
  });
}

/** The edge table for the context's viewer (`domain/lifecycle-edges.ts`). */
export function edgesOf(context: Context): Edge[] {
  return lifecycleEdges(edgeFactsOf(context));
}

/**
 * **THE ACTIVITY AXIS, in the "…" sheet** (Leon, 2026-11-03): Start while the
 * item is idle, Pause while it is active, each with its resolver row, so the
 * axis stays reachable without being the default gesture. A flow member whose
 * stage `means` apply `active` has the stage move as its way instead, and a
 * concluded item has neither.
 */
export function activityRows({
  itemId,
  context,
  flows = [],
}: {
  itemId: string;
  context: Context;
  flows?: readonly { rows: readonly StageFacts[] }[];
}): ActRow[] {
  const state = context.item;
  if (state.closed) return [];
  const start: PendingStimulus = { kind: 'act', target: { attribute: 'active', value: 'yes' } };
  const pause: PendingStimulus = { kind: 'act', target: { attribute: 'active', value: 'no' } };
  const member = context.memberships.length > 0;
  const covered = member && meansCover(flows[0]?.rows ?? [], 'active');
  const out = state.active
    ? [row('pause', pause, itemId, context)]
    : covered
      ? []
      : [row('start', start, itemId, context)];
  return out.filter((r) => !r.noChange);
}

/**
 * ONE AXIS'S VALUES, with the dry run's answer about each (Leon, 2026-09-21:
 * the line wears values). The worn one is checked and inert; every other one
 * carries what moving there would mean - a plan, a question, or the refusal's
 * own words.
 */
export function attributeOffers(
  attribute: LaneAttribute,
  itemId: string,
  context: Context,
): ValueOffer[] {
  const worn = laneValueOf(attribute, context.item);
  return LANE_ATTRIBUTE_VALUES[attribute].map((value) => {
    if (value.id === worn) {
      return {
        attribute,
        value: value.id,
        label: value.label,
        worn: true,
        offer: { enabled: false, noChange: true },
      };
    }
    return {
      attribute,
      value: value.id,
      label: value.label,
      worn: false,
      offer: offerOf(
        dryRun({ kind: 'act', target: { attribute, value: value.id } }, itemId, context),
      ),
    };
  });
}

/** The grade `decide` would stamp on THIS viewer's Accept or Deny - the
 *  word the review composer's two buttons wear. From the dry run, like every
 *  other row. */
export function reviewLevel(
  itemId: string,
  context: Context,
  edge: 'accept' | 'deny' = 'accept',
): OutOfRoleLevel | undefined {
  return outOfRoleLevel(offerOf(dryRun({ kind: 'edge', edge }, itemId, context)).level);
}
